<template>
  <div class="company-stats" :class="{ 'company-stats--refreshing': loading && summary }">
    <div class="company-stats__toolbar">
      <div class="segmented" role="group" aria-label="Период">
        <button
          v-for="item in periods"
          :key="item.value"
          type="button"
          class="segmented__button"
          :class="{ 'segmented__button--active': period === item.value }"
          :aria-pressed="period === item.value ? 'true' : 'false'"
          @click="setPeriod(item.value)"
        >
          {{ item.text }}
        </button>
      </div>
      <div v-if="summary" class="company-stats__caption">{{ periodCaption }}</div>
    </div>

    <div v-if="!summary && error" class="company-stats__empty">
      <b-icon icon="exclamation-triangle" font-scale="2"></b-icon>
      <div class="company-stats__empty-title">Не удалось загрузить статистику</div>
      <button type="button" class="company-stats__link-button" @click="loadSummary">Повторить</button>
    </div>

    <div v-else-if="!summary" class="company-stats__loader">
      <b-spinner variant="success" small></b-spinner>
      <span>Собираем статистику…</span>
    </div>

    <template v-else>
      <div v-if="!summary.first_invoice_date" class="company-stats__empty">
        <b-icon icon="bar-chart" font-scale="2"></b-icon>
        <div class="company-stats__empty-title">{{ texts.noInvoicesTitle }}</div>
        <div class="company-stats__empty-text">{{ texts.noInvoicesHint }}</div>
      </div>

      <div v-else-if="!summary.kpi.invoiced_count" class="company-stats__empty">
        <b-icon icon="calendar-x" font-scale="2"></b-icon>
        <div class="company-stats__empty-title">За выбранный период счетов нет</div>
        <div class="company-stats__empty-text">
          Последний счёт — {{ formatDate(summary.last_invoice_date) }}
        </div>
        <button type="button" class="company-stats__link-button" @click="setPeriod('all')">
          Показать за всё время
        </button>
      </div>

      <template v-else>
        <div class="stat-tiles">
          <div v-for="tile in tiles" :key="tile.key" class="stat-tile">
            <div class="stat-tile__label">{{ tile.label }}</div>
            <div class="stat-tile__value" :title="tile.title">{{ tile.value }}</div>

            <div
              v-if="tile.meter !== undefined"
              class="meter"
              role="meter"
              aria-valuemin="0"
              aria-valuemax="100"
              :aria-valuenow="Math.round(tile.meter * 100)"
            >
              <div class="meter__fill" :style="{ width: `${Math.round(tile.meter * 100)}%` }"></div>
            </div>

            <div class="stat-tile__footer">
              <span class="stat-tile__sub">
                <b-icon
                  v-if="tile.status"
                  :icon="tile.status === 'good' ? 'check-circle-fill' : 'exclamation-circle-fill'"
                  :class="`stat-tile__status stat-tile__status--${tile.status}`"
                ></b-icon>
                {{ tile.sub }}
              </span>
              <span
                v-if="tile.delta"
                class="delta"
                :class="`delta--${tile.delta.tone}`"
                :title="tile.delta.title"
              >
                {{ tile.delta.text }}
              </span>
            </div>
          </div>
        </div>

        <section class="stats-card">
          <header class="stats-card__header">
            <div>
              <h6 class="stats-card__title">{{ texts.chartTitle }}</h6>
              <div class="stats-card__sub">
                По {{ summary.period.granularity === 'quarter' ? 'кварталам' : 'месяцам' }}, по дате счёта
              </div>
            </div>
            <div class="segmented segmented--small" role="group" aria-label="Вид">
              <button
                type="button"
                class="segmented__button"
                :class="{ 'segmented__button--active': view === 'chart' }"
                @click="view = 'chart'"
              >
                График
              </button>
              <button
                type="button"
                class="segmented__button"
                :class="{ 'segmented__button--active': view === 'table' }"
                @click="view = 'table'"
              >
                Таблица
              </button>
            </div>
          </header>

          <template v-if="view === 'chart'">
            <div class="legend">
              <span class="legend__item">
                <i class="legend__swatch" :style="{ background: colors.invoiced }"></i>
                {{ texts.invoicedSeries }}
              </span>
              <span class="legend__item">
                <i class="legend__swatch" :style="{ background: colors.paid }"></i>
                {{ texts.paidSeries }}
              </span>
            </div>
            <div class="stats-chart">
              <StatisticBarGraph
                :chart-data="chartData"
                :chart-options="chartOptions"
                :styles="{ position: 'relative', height: '100%' }"
              />
            </div>
          </template>

          <div v-else class="stats-table-wrap">
            <table class="stats-table">
              <thead>
                <tr>
                  <th>Период</th>
                  <th class="text-right">Счетов</th>
                  <th class="text-right">{{ texts.invoicedSeries }}</th>
                  <th class="text-right">{{ texts.paidSeries }}</th>
                  <th class="text-right">Оплачено, %</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="bucket in summary.timeline"
                  :key="bucket.key"
                  :class="{ 'stats-table__row--empty': !bucket.count }"
                >
                  <td>{{ bucket.label }}</td>
                  <td class="text-right">{{ bucket.count }}</td>
                  <td class="text-right">{{ formatMoney(bucket.invoiced) }}</td>
                  <td class="text-right">{{ formatMoney(bucket.paid) }}</td>
                  <td class="text-right">{{ formatShare(bucket.invoiced - bucket.debt, bucket.invoiced) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Итого</td>
                  <td class="text-right">{{ summary.kpi.invoiced_count }}</td>
                  <td class="text-right">{{ formatMoney(summary.kpi.invoiced_sum) }}</td>
                  <td class="text-right">{{ formatMoney(summary.kpi.paid_sum) }}</td>
                  <td class="text-right">{{ formatShare(summary.kpi.invoiced_sum - summary.kpi.debt_sum, summary.kpi.invoiced_sum) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </section>

        <div class="stats-grid">
          <section class="stats-card">
            <header class="stats-card__header">
              <div>
                <h6 class="stats-card__title">Услуги</h6>
                <div class="stats-card__sub">Сумма счетов по услугам заказа</div>
              </div>
            </header>
            <ul class="bar-list">
              <li v-for="item in summary.services" :key="item.name" class="bar-list__item">
                <div class="bar-list__row">
                  <span class="bar-list__name" :title="item.name">{{ item.name }}</span>
                  <span class="bar-list__value" :title="formatMoney(item.sum)">{{ formatMoneyCompact(item.sum) }}</span>
                </div>
                <div class="bar-list__track">
                  <div
                    class="bar-list__fill"
                    :class="{ 'bar-list__fill--other': item.is_other }"
                    :style="{ width: barWidth(item.sum, summary.services) }"
                  ></div>
                </div>
                <div class="bar-list__meta">
                  {{ item.count }} {{ plural(item.count, ['счёт', 'счёта', 'счетов']) }}
                  · {{ formatShare(item.sum, summary.kpi.invoiced_sum) }}
                </div>
              </li>
            </ul>
          </section>

          <section class="stats-card">
            <header class="stats-card__header">
              <div>
                <h6 class="stats-card__title">{{ texts.counterpartiesTitle }}</h6>
                <div class="stats-card__sub">{{ counterpartiesCaption }}</div>
              </div>
            </header>
            <ul v-if="summary.counterparties.items.length" class="bar-list">
              <li v-for="item in summary.counterparties.items" :key="item.id || item.name" class="bar-list__item">
                <div class="bar-list__row">
                  <span class="bar-list__name" :title="item.name">{{ item.name }}</span>
                  <span class="bar-list__value" :title="formatMoney(item.sum)">{{ formatMoneyCompact(item.sum) }}</span>
                </div>
                <div class="bar-list__track">
                  <div
                    class="bar-list__fill"
                    :class="{ 'bar-list__fill--other': item.is_other }"
                    :style="{ width: barWidth(item.sum, summary.counterparties.items) }"
                  ></div>
                </div>
                <div class="bar-list__meta">
                  {{ item.count }} {{ plural(item.count, ['счёт', 'счёта', 'счетов']) }}
                  <template v-if="item.orders !== null">
                    · {{ item.orders }} {{ plural(item.orders, ['заказ', 'заказа', 'заказов']) }}
                  </template>
                </div>
              </li>
            </ul>
            <div v-else class="stats-card__placeholder">{{ texts.noCounterparties }}</div>
          </section>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { API_GET_FINANCE_COMPANY_STATISTICS_SUMMARY } from '@/constants';
import StatisticBarGraph from '@components/charts/StatisticBarGraph';

const PERIOD_STORAGE_KEY = 'companyStatisticsPeriod';

// Пара проверена на различимость при дальтонизме; у оранжевого контраст < 3:1,
// поэтому у графика всегда есть легенда и табличный вид
const COLORS = {
  invoiced: '#ea7b27',
  invoicedHover: '#f09a5c',
  paid: '#1a8a64',
  paidHover: '#3aa37d',
  ink: '#0b0b0b',
  inkSecondary: '#52514e',
  muted: '#898781',
  grid: '#e1e0d9',
  baseline: '#c3c2b7',
};

const PERIOD_LABELS = {
  quarter: '3 мес.',
  half: '6 мес.',
  year: '12 мес.',
};

const moneyFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const decimalFormatter = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 });

export default {
  components: { StatisticBarGraph },

  props: {
    companyId: {
      type: Number | String,
      required: true
    },
    additionalGetParameter: {
      type: String,
      default: ''
    },
    isTransportCompany: {
      type: Boolean,
      default: false
    },
  },

  data() {
    return {
      periods: [
        { value: 'quarter', text: '3 месяца' },
        { value: 'half', text: '6 месяцев' },
        { value: 'year', text: 'Год' },
        { value: 'all', text: 'Всё время' },
      ],
      period: localStorage.getItem(PERIOD_STORAGE_KEY) || 'half',
      view: 'chart',
      summary: null,
      loading: false,
      error: false,
      requestId: 0,
      colors: COLORS,
    }
  },

  computed: {
    texts() {
      if (this.isTransportCompany) {
        return {
          invoiced: 'Счета от компании',
          paid: 'Оплачено компании',
          debt: 'Наш долг',
          orders: 'Заказов как подрядчик',
          avgInvoice: 'Средний счёт',
          paymentDays: 'Срок оплаты нами',
          invoicedSeries: 'Выставлено нам',
          paidSeries: 'Оплачено',
          chartTitle: 'Счета от компании и оплаты',
          counterpartiesTitle: 'Клиенты, чьи заказы везла компания',
          counterpartiesUnit: ['клиент', 'клиента', 'клиентов'],
          noCounterparties: 'Клиенты не найдены',
          noInvoicesTitle: 'Счетов от компании пока нет',
          noInvoicesHint: 'Статистика появится, когда компанию укажут поставщиком в счёте заказа.',
        };
      }

      return {
        invoiced: 'Выставлено',
        paid: 'Оплачено',
        debt: 'Задолженность',
        orders: 'Заказов',
        avgInvoice: 'Средний счёт',
        paymentDays: 'Срок оплаты',
        invoicedSeries: 'Выставлено',
        paidSeries: 'Оплачено',
        chartTitle: 'Выставлено и оплачено',
        counterpartiesTitle: 'Подрядчики по заказам компании',
        counterpartiesUnit: ['подрядчик', 'подрядчика', 'подрядчиков'],
        noCounterparties: 'По заказам компании ещё нет счетов от подрядчиков',
        noInvoicesTitle: 'Компании ещё не выставляли счета',
        noInvoicesHint: 'Статистика появится после первого счёта покупателю.',
      };
    },

    periodCaption() {
      const { start, end } = this.summary.period;
      const range = `${this.formatDate(start)} — ${this.formatDate(end)}`;

      return PERIOD_LABELS[this.period]
        ? `${range} · сравнение с предыдущими ${PERIOD_LABELS[this.period]}`
        : range;
    },

    counterpartiesCaption() {
      const { total_count, total_sum } = this.summary.counterparties;
      if (!total_count) {
        return 'За выбранный период';
      }

      return `${total_count} ${this.plural(total_count, this.texts.counterpartiesUnit)} · ${this.formatMoneyCompact(total_sum)}`;
    },

    tiles() {
      const kpi = this.summary.kpi;
      const upIsGood = this.isTransportCompany ? null : true;

      return [
        {
          key: 'invoiced',
          label: this.texts.invoiced,
          value: this.formatMoneyCompact(kpi.invoiced_sum),
          title: this.formatMoney(kpi.invoiced_sum),
          sub: `${kpi.invoiced_count} ${this.plural(kpi.invoiced_count, ['счёт', 'счёта', 'счетов'])}`,
          delta: this.delta('invoiced_sum', upIsGood),
        },
        {
          key: 'paid',
          label: this.texts.paid,
          value: this.formatMoneyCompact(kpi.paid_sum),
          title: this.formatMoney(kpi.paid_sum),
          meter: kpi.paid_share || 0,
          sub: `${this.formatShare(kpi.invoiced_sum - kpi.debt_sum, kpi.invoiced_sum)} суммы оплачено`,
          delta: this.delta('paid_sum', upIsGood),
        },
        {
          key: 'debt',
          label: this.texts.debt,
          value: this.formatMoneyCompact(kpi.debt_sum),
          title: this.formatMoney(kpi.debt_sum),
          status: kpi.unpaid_count ? 'warning' : 'good',
          sub: kpi.unpaid_count
            ? `${kpi.unpaid_count} ${this.plural(kpi.unpaid_count, ['неоплаченный счёт', 'неоплаченных счёта', 'неоплаченных счетов'])}`
            : 'Все счета оплачены',
          delta: this.delta('debt_sum', false),
        },
        {
          key: 'orders',
          label: this.texts.orders,
          value: kpi.orders_count,
          sub: 'по которым есть счета',
          delta: this.delta('orders_count', upIsGood),
        },
        {
          key: 'avg_invoice',
          label: this.texts.avgInvoice,
          value: this.formatMoneyCompact(kpi.avg_invoice),
          title: this.formatMoney(kpi.avg_invoice),
          sub: 'сумма одного счёта',
          delta: this.delta('avg_invoice', upIsGood),
        },
        {
          key: 'payment_days',
          label: this.texts.paymentDays,
          value: kpi.avg_payment_days === null ? '—' : `${decimalFormatter.format(kpi.avg_payment_days)} дн.`,
          sub: 'от даты счёта до оплаты',
          delta: this.delta('avg_payment_days', this.isTransportCompany ? null : false, 'days'),
        },
      ];
    },

    chartData() {
      const timeline = this.summary.timeline;

      return {
        labels: timeline.map(bucket => bucket.label),
        datasets: [
          {
            label: this.texts.invoicedSeries,
            data: timeline.map(bucket => bucket.invoiced),
            backgroundColor: COLORS.invoiced,
            hoverBackgroundColor: COLORS.invoicedHover,
            borderWidth: 0,
            maxBarThickness: 24,
          },
          {
            label: this.texts.paidSeries,
            data: timeline.map(bucket => bucket.paid),
            backgroundColor: COLORS.paid,
            hoverBackgroundColor: COLORS.paidHover,
            borderWidth: 0,
            maxBarThickness: 24,
          },
        ],
      };
    },

    chartOptions() {
      const timeline = this.summary.timeline;
      const fontFamily = window.getComputedStyle(document.body).fontFamily;
      const ticks = { fontColor: COLORS.muted, fontFamily, fontSize: 11 };

      return {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        legend: { display: false },
        layout: { padding: { top: 8 } },
        scales: {
          xAxes: [{
            categoryPercentage: 0.7,
            barPercentage: 0.9,
            gridLines: { display: false, drawBorder: false },
            ticks: { ...ticks, maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
          }],
          yAxes: [{
            gridLines: {
              color: COLORS.grid,
              zeroLineColor: COLORS.baseline,
              drawBorder: false,
              drawTicks: false,
            },
            ticks: {
              ...ticks,
              beginAtZero: true,
              maxTicksLimit: 5,
              padding: 8,
              callback: value => this.formatCompact(value),
            },
          }],
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: '#ffffff',
          borderColor: 'rgba(11, 11, 11, 0.12)',
          borderWidth: 1,
          titleFontColor: COLORS.ink,
          titleFontFamily: fontFamily,
          bodyFontColor: COLORS.inkSecondary,
          bodyFontFamily: fontFamily,
          footerFontColor: COLORS.muted,
          footerFontFamily: fontFamily,
          footerFontStyle: 'normal',
          xPadding: 12,
          yPadding: 10,
          caretSize: 0,
          cornerRadius: 6,
          multiKeyBackground: '#ffffff',
          callbacks: {
            label: (item, data) => `${data.datasets[item.datasetIndex].label}: ${this.formatMoney(item.yLabel)}`,
            footer: items => {
              const bucket = timeline[items[0].index];
              return bucket.count
                ? `${bucket.count} ${this.plural(bucket.count, ['счёт', 'счёта', 'счетов'])} · оплачено ${this.formatShare(bucket.invoiced - bucket.debt, bucket.invoiced)}`
                : 'Счетов нет';
            },
          },
        },
      };
    },
  },

  methods: {
    loadSummary() {
      const requestId = ++this.requestId;
      this.loading = true;
      this.error = false;

      api.call('get', `${API_GET_FINANCE_COMPANY_STATISTICS_SUMMARY}${this.additionalGetParameter}&period=${this.period}`)
        .then(({ data }) => {
          if (requestId === this.requestId) {
            this.summary = data;
          }
        })
        .catch(() => {
          if (requestId === this.requestId) {
            this.error = true;
            this.$toast.error('Не удалось загрузить статистику компании');
          }
        })
        .finally(() => {
          if (requestId === this.requestId) {
            this.loading = false;
          }
        });
    },

    setPeriod(period) {
      if (this.period === period) {
        return;
      }

      this.period = period;
      localStorage.setItem(PERIOD_STORAGE_KEY, period);
      this.loadSummary();
    },

    // upIsGood: true — рост хорошо, false — рост плохо, null — нейтрально
    delta(key, upIsGood, unit = 'percent') {
      if (!this.summary.previous) {
        return null;
      }

      const current = this.summary.kpi[key];
      const previous = this.summary.previous.kpi[key];
      if (current === null || previous === null || previous === undefined) {
        return null;
      }

      const diff = current - previous;
      let text;

      if (unit === 'days') {
        if (Math.abs(diff) < 0.5) {
          return { text: 'без изменений', tone: 'neutral', title: `Было: ${decimalFormatter.format(previous)} дн.` };
        }
        text = `${diff > 0 ? '▲' : '▼'} ${decimalFormatter.format(Math.abs(diff))} дн.`;
      } else {
        if (!previous) {
          return null;
        }
        const percent = diff / Math.abs(previous) * 100;
        if (Math.abs(percent) < 0.5) {
          return { text: 'без изменений', tone: 'neutral', title: this.previousTitle(key, previous) };
        }
        text = `${percent > 0 ? '▲' : '▼'} ${moneyFormatter.format(Math.abs(percent))}%`;
      }

      const tone = upIsGood === null ? 'neutral' : ((diff > 0) === upIsGood ? 'good' : 'bad');

      return { text, tone, title: unit === 'days' ? `Было: ${decimalFormatter.format(previous)} дн.` : this.previousTitle(key, previous) };
    },

    previousTitle(key, previous) {
      const value = key === 'orders_count' ? previous : this.formatMoney(previous);
      return `За предыдущий период: ${value}`;
    },

    barWidth(value, items) {
      const max = Math.max(...items.map(item => item.sum));
      return max > 0 ? `${Math.max(value / max * 100, 1)}%` : '0%';
    },

    plural(number, forms) {
      const n = Math.abs(number) % 100;
      const n1 = n % 10;
      if (n > 10 && n < 20) return forms[2];
      if (n1 > 1 && n1 < 5) return forms[1];
      if (n1 === 1) return forms[0];
      return forms[2];
    },

    formatMoney(value) {
      return value === null || value === undefined ? '—' : `${moneyFormatter.format(value)} ₽`;
    },

    formatCompact(value) {
      const abs = Math.abs(value);
      if (abs >= 1e6) return `${decimalFormatter.format(value / 1e6)} млн`;
      if (abs >= 1e3) return `${moneyFormatter.format(value / 1e3)} тыс`;
      return moneyFormatter.format(value);
    },

    formatMoneyCompact(value) {
      return value === null || value === undefined ? '—' : `${this.formatCompact(value)} ₽`;
    },

    formatShare(part, total) {
      return total > 0 ? `${moneyFormatter.format(Math.min(part / total, 1) * 100)}%` : '—';
    },

    formatDate(date) {
      if (!date) {
        return '—';
      }
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year}`;
    },
  },

  mounted() {
    if (!this.periods.some(item => item.value === this.period)) {
      this.period = 'half';
    }
    this.loadSummary();
  },
}
</script>

<style lang="scss" scoped>
$ink: #0b0b0b;
$ink-secondary: #52514e;
$muted: #898781;
$hairline: rgba(11, 11, 11, 0.1);
$plane: #f9f9f7;
$brand: #0d6247;
$invoiced: #ea7b27;
$paid: #1a8a64;
$paid-track: #d6eee5;
$track: #efeee9;
$good: #006300;
$bad: #d03b3b;
$warning: #fab219;
$status-good: #0ca30c;

.company-stats {
  color: $ink;
  transition: opacity 0.2s;

  &--refreshing {
    opacity: 0.55;
    pointer-events: none;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    margin-bottom: 16px;
  }

  &__caption {
    color: $muted;
    font-size: 0.8125rem;
  }

  &__loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 240px;
    color: $muted;
  }

  &__empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 48px 16px;
    border: 1px dashed $hairline;
    border-radius: 12px;
    background: $plane;
    color: $muted;
    text-align: center;
  }

  &__empty-title {
    margin-top: 6px;
    color: $ink;
    font-weight: 600;
  }

  &__empty-text {
    font-size: 0.875rem;
  }

  &__link-button {
    margin-top: 8px;
    padding: 4px 12px;
    border: 1px solid $brand;
    border-radius: 6px;
    background: transparent;
    color: $brand;
    font-size: 0.875rem;

    &:hover {
      background: rgba(13, 98, 71, 0.06);
    }
  }
}

.segmented {
  display: inline-flex;
  padding: 2px;
  border-radius: 8px;
  background: $track;

  &__button {
    padding: 5px 14px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: $ink-secondary;
    font-size: 0.875rem;
    white-space: nowrap;
    transition: background 0.15s, color 0.15s;

    &:hover {
      color: $ink;
    }

    &:focus-visible {
      outline: 2px solid $brand;
      outline-offset: 1px;
    }

    &--active {
      background: #fff;
      color: $brand;
      font-weight: 600;
      box-shadow: 0 1px 2px rgba(11, 11, 11, 0.08);
    }
  }

  &--small &__button {
    padding: 3px 10px;
    font-size: 0.8125rem;
  }
}

.stat-tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  @media screen and (max-width: 767px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.stat-tile {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px;
  border: 1px solid $hairline;
  border-radius: 12px;
  background: #fff;

  &__label {
    color: $ink-secondary;
    font-size: 0.8125rem;
  }

  &__value {
    font-size: 1.5rem;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 4px 8px;
    margin-top: auto;
  }

  &__sub {
    color: $muted;
    font-size: 0.75rem;
  }

  &__status {
    margin-right: 2px;

    &--good {
      color: $status-good;
    }

    &--warning {
      color: $warning;
    }
  }
}

.meter {
  height: 6px;
  border-radius: 3px;
  background: $paid-track;
  overflow: hidden;

  &__fill {
    height: 100%;
    border-radius: 3px;
    background: $paid;
    transition: width 0.3s;
  }
}

.delta {
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;

  &--good {
    color: $good;
  }

  &--bad {
    color: $bad;
  }

  &--neutral {
    color: $ink-secondary;
  }
}

.stats-card {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid $hairline;
  border-radius: 12px;
  background: #fff;

  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
  }

  &__title {
    margin: 0;
    font-size: 0.9375rem;
    font-weight: 600;
  }

  &__sub {
    margin-top: 2px;
    color: $muted;
    font-size: 0.75rem;
  }

  &__placeholder {
    padding: 24px 0;
    color: $muted;
    font-size: 0.875rem;
    text-align: center;
  }
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
  color: $ink-secondary;
  font-size: 0.8125rem;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  &__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 2px;
  }
}

.stats-chart {
  position: relative;
  height: 280px;
}

.stats-table-wrap {
  max-height: 320px;
  overflow: auto;
}

.stats-table {
  width: 100%;
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid $hairline;
    white-space: nowrap;
  }

  th {
    position: sticky;
    top: 0;
    background: #fff;
    color: $muted;
    font-weight: 400;
  }

  tfoot td {
    border-bottom: 0;
    font-weight: 600;
  }

  &__row--empty td {
    color: $muted;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 0 16px;
}

.bar-list {
  margin: 0;
  padding: 0;
  list-style: none;

  &__item + &__item {
    margin-top: 12px;
  }

  &__row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 0.875rem;
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__value {
    flex-shrink: 0;
    font-weight: 600;
  }

  &__track {
    height: 6px;
    margin: 4px 0 3px;
    border-radius: 3px;
    background: $track;
  }

  &__fill {
    height: 100%;
    border-radius: 3px;
    background: $invoiced;
    transition: width 0.3s;

    &--other {
      background: #c3c2b7;
    }
  }

  &__meta {
    color: $muted;
    font-size: 0.75rem;
  }
}
</style>
